import type { ClientView, ConversationSummary } from '@lobby/shared'

// Spec 3.4 contracts, from packages/shared.
export { CHANNEL_KINDS } from '@lobby/shared'
export type { ChannelKind, ChannelState, ChannelStatus, EmbeddedSignupSetup, MetaPageOption, WhatsAppSignupConfig } from '@lobby/shared'
import { CHANNEL_KINDS, type ChannelKind, type ChannelStatus, type ConnectMetaBody } from '@lobby/shared'

/** POST /api/channels/meta/connect. `pick` (the login state) is added by the flow from the callback URL. */
export type MetaConnectBody = Omit<ConnectMetaBody, 'pick'> & { pick?: string }

/** Channels the assistant answers on. Messenger is shown as "Facebook" (Messenger and Page comments). */
export const CHANNELS = CHANNEL_KINDS
export type Channel = ChannelKind
export type ChannelFilter = Channel | 'all'

/**
 * Meta's own automatic replies (WhatsApp Business app greeting and away messages, Instant Replies and away messages in
 * Meta Business Suite) would answer next to the assistant. Shown in the WhatsApp step and the Facebook & Instagram connect.
 */
export const AUTO_REPLY_WARNING =
  'Turn off greeting and away messages in the WhatsApp Business app and Instant Replies in Meta Business Suite; Lobby answers instead.'

export const CHANNEL_LABEL: Record<Channel, string> = { whatsapp: 'WhatsApp', messenger: 'Facebook', instagram: 'Instagram' }

type HasChannel = { channel?: ConversationSummary['channel'] | ChannelKind }

/** Channel of a conversation. Conversations without a `channel` are WhatsApp. */
export const channelOf = (c: HasChannel): Channel => c.channel ?? 'whatsapp'

export const matchesChannel = (c: HasChannel, f: ChannelFilter) => f === 'all' || channelOf(c) === f

const emptyChannel = (kind: ChannelKind): ChannelStatus => ({ kind, state: 'not_connected', externalId: null, displayName: null })

/** One status per kind, in display order. Kinds the Worker did not report are `not_connected`. */
export function channelsOf(client: ClientView | null | undefined): ChannelStatus[] {
  const list = client?.channels ?? []
  return CHANNEL_KINDS.map((kind) => list.find((c) => c.kind === kind) ?? emptyChannel(kind))
}

export const channelOfKind = (client: ClientView | null | undefined, kind: ChannelKind): ChannelStatus =>
  channelsOf(client).find((c) => c.kind === kind) ?? emptyChannel(kind)

/** The channel is linked to an account (it may still need attention, such as a card or a reconnect). */
export const isLinked = (c: ChannelStatus) => c.state !== 'not_connected' && c.state !== 'disconnected'

/** WhatsApp is connected but the owner has not confirmed a card in Meta Billing yet. */
export const needsBilling = (c: ChannelStatus) =>
  c.kind === 'whatsapp' && (c.state === 'needs_billing' || (c.state === 'connected' && c.billingConfirmedAt === null))

/** The assistant can answer on this channel right now. */
export const isAnswering = (c: ChannelStatus) => c.state === 'connected' && !needsBilling(c)

/** Something the owner has to fix: reconnect, add a card, or sort out a Meta payment. */
export const needsAttention = (c: ChannelStatus) => c.state === 'token_invalid' || c.state === 'payment_issue' || needsBilling(c)

export type PillTone = 'green' | 'amber' | 'grey' | 'red'

/** Status pill for a channel. A connected WhatsApp without a confirmed card shows "Card needed". */
export function channelPill(c: ChannelStatus): { tone: PillTone; label: string } {
  if (needsBilling(c)) return { tone: 'amber', label: 'Card needed' }
  switch (c.state) {
    case 'connected':
      return { tone: 'green', label: 'Connected' }
    case 'token_invalid':
      return { tone: 'red', label: 'Reconnect needed' }
    case 'payment_issue':
      return { tone: 'red', label: 'Meta payment problem' }
    case 'disconnected':
      return { tone: 'grey', label: 'Disconnected' }
    default:
      return { tone: 'grey', label: 'Not connected' }
  }
}

/** Sidebar line under the business name: which channels answer. */
export function channelSummary(client: ClientView | null | undefined): string {
  const on = channelsOf(client).filter(isAnswering).map((c) => CHANNEL_LABEL[c.kind])
  if (on.length === 0) return 'No channel connected'
  if (on.length === 1) return `${on[0]} connected`
  return `${on.slice(0, -1).join(', ')} and ${on[on.length - 1]}`
}
